import React, { useState } from 'react';

import { Button, Modal, MultiSelect, Space, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconPlus, IconUsersGroup } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError, HttpStatusCode } from 'axios';
import { ErrorAlert } from 'components/ErrorAlert';
import { NoDataFound } from 'components/NoDataFound';
import { VirtualGridScrollArea } from 'components/VirtualGridScrollArea';
import { useUserContext } from 'contexts/UserContext';
import { OrganisationSectionWrapper } from 'pages/Organisation/OrganisationSectionWrapper';
import { TeamCard } from 'pages/Organisation/TeamCard';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { OrganisationService } from 'services/organisation-service';
import { TeamService } from 'services/team-service';
import { ALL_USERS_INFO_QUERY_KEY, MY_TEAMS_QUERY_KEY } from 'shared/constants/query-keys';
import { CreateTeamForm, Team } from 'shared/interfaces/Team';
import { UserAccess } from 'shared/interfaces/User';
import {
  TEAM_MAX_NUM_MEMBERS,
  TEAM_NAME_MAX_LENGTH,
  maxLengthValidator,
  maxStringArrayLengthValidator,
  nonEmptyArrayValidator,
  requiredInputValidator
} from 'shared/utils/form-validators';

export function OrganisationTeamsTab() {
  const { t } = useTranslation();
  const [isCreateTeamModalOpen, setIsCreateTeamModalOpen] = useState<boolean>(false);
  const [loggedInUser] = useUserContext();
  const [isCreatingTeam, setIsCreatingTeam] = useState<boolean>(false);
  const queryClient = useQueryClient();

  // Queries
  const myTeamsQuery = useQuery({
    queryKey: [MY_TEAMS_QUERY_KEY],
    queryFn: loggedInUser.role === UserAccess.SUPER_ADMIN ? TeamService.getAllTeams : TeamService.getOwnedTeams,
    onError: () => NotificationService.showResult(false, t('FEED_BACK.TEAMS.FAILURE.GET.UNKNOWN'))
  });

  const allUsersQuery = useQuery({
    queryKey: [ALL_USERS_INFO_QUERY_KEY],
    queryFn: OrganisationService.getAllUsers
  });

  // Mutations
  const createTeamMutation = useMutation({
    mutationFn: async (createTeamForm: CreateTeamForm) => {
      if (!allUsersQuery.data) throw Error(t('FEED_BACK.TEAMS.FAILURE.CREATE.UNKNOWN'));

      const selectedUsersIds = createTeamForm.membersEmails.reduce((userIds: string[], email: string) => {
        const user = allUsersQuery.data.find((u) => u.email === email);
        if (user) {
          userIds.push(user._id);
        }
        return userIds;
      }, []);

      return TeamService.createTeam({
        name: createTeamForm.name,
        members: selectedUsersIds
      });
    },
    onMutate: () => setIsCreatingTeam(true),
    onSuccess: (newTeam: Team) => {
      setIsCreatingTeam(false);
      NotificationService.showResult(true, t('FEED_BACK.TEAMS.SUCCESS.CREATE'));

      // Add the new team to the previous list of teams
      queryClient.setQueryData([MY_TEAMS_QUERY_KEY], (existingTeams: Team[] = []) => {
        return [...existingTeams, newTeam];
      });

      closeCreateTeamModalAndResetForm();
    },
    onError: (error: AxiosError) => {
      setIsCreatingTeam(false);
      NotificationService.showResult(
        false,
        t(
          error.response?.status == HttpStatusCode.Conflict
            ? 'FEED_BACK.TEAMS.FAILURE.CREATE.CONFLICT'
            : 'FEED_BACK.TEAMS.FAILURE.CREATE.UNKNOWN'
        )
      );
    }
  });

  // Open/Close modals
  const openCreateTeamModal = () => setIsCreateTeamModalOpen(true);
  const closeCreateTeamModal = () => setIsCreateTeamModalOpen(false);
  const closeCreateTeamModalAndResetForm = () => {
    closeCreateTeamModal();
    createNewTeamForm.reset();
  };

  // Forms
  const createNewTeamForm = useForm({
    initialValues: {
      name: '',
      membersEmails: []
    },
    validate: {
      name: (value) => requiredInputValidator(value) || maxLengthValidator(value, TEAM_NAME_MAX_LENGTH),
      membersEmails: (value) =>
        nonEmptyArrayValidator(value) || maxStringArrayLengthValidator(value, TEAM_MAX_NUM_MEMBERS)
    }
  });

  if (myTeamsQuery.isError || allUsersQuery.error) return <ErrorAlert />;

  const isLoadingQueries =
    myTeamsQuery.isLoading || !myTeamsQuery.data || allUsersQuery.isLoading || !allUsersQuery.data;

  const CreateButton = () => (
    <Button
      leftIcon={<IconPlus size={22} />}
      onClick={() => {
        openCreateTeamModal();
      }}>
      {t('APP.ACTIONS.CREATE')}
    </Button>
  );

  return (
    <>
      <OrganisationSectionWrapper
        title={t('ORGANISATION.TEAMS.SUBTITLE')}
        rightComponent={!isLoadingQueries && myTeamsQuery.data.length === 0 ? undefined : <CreateButton />}>
        {isLoadingQueries ? (
          <></>
        ) : (
          <VirtualGridScrollArea
            h={myTeamsQuery.data.length === 0 ? 0 : '100%'}
            items={myTeamsQuery.data.map((team, key) => (
              <TeamCard key={key} mb={5} mr={10} team={team} allUsers={allUsersQuery.data} />
            ))}
          />
        )}

        {!isLoadingQueries && myTeamsQuery.data.length === 0 && (
          <NoDataFound
            h="100%"
            title={t('NO_DATA_FOUND.TEAMS.TITLE')}
            subTitle={t('NO_DATA_FOUND.TEAMS.SUB_TITLE')}
            icon={<IconUsersGroup />}
            button={<CreateButton />}
          />
        )}
      </OrganisationSectionWrapper>

      <Modal
        title={t('ORGANISATION.TEAMS.CREATE_MODAL.TITLE')}
        onClose={closeCreateTeamModalAndResetForm}
        opened={isCreateTeamModalOpen}>
        <TextInput
          label={t('ORGANISATION.TEAMS.NAME_LABEL')}
          placeholder={t('ORGANISATION.TEAMS.NAME_PLACEHOLDER')}
          {...createNewTeamForm.getInputProps('name')}
        />
        <Space h="md" />
        <MultiSelect
          sx={{
            '.mantine-MultiSelect-input': {
              maxHeight: '250px',
              overflowY: 'auto'
            }
          }}
          data={
            // Can't select super admin or current logged in user to add to a team that's being created
            allUsersQuery.data
              ?.filter((user) => user.role !== UserAccess.SUPER_ADMIN && user._id !== loggedInUser._id)
              .map((user) => user.email) || []
          }
          label={t('ORGANISATION.TEAMS.CREATE_MODAL.MEMBERS_LABEL')}
          placeholder={t('ORGANISATION.TEAMS.CREATE_MODAL.MEMBERS_PLACEHOLDER')}
          searchable
          nothingFound={t('ORGANISATION.TEAMS.CREATE_MODAL.NO_USERS_FOUND')}
          maxSelectedValues={TEAM_MAX_NUM_MEMBERS}
          {...createNewTeamForm.getInputProps('membersEmails')}
        />

        <Space h="md" />

        <Button
          my="xs"
          style={{ float: 'right' }}
          loading={isCreatingTeam}
          onClick={() => {
            createNewTeamForm.validate();
            if (createNewTeamForm.isValid()) {
              createTeamMutation.mutate({
                name: createNewTeamForm.values.name,
                membersEmails: createNewTeamForm.values.membersEmails
              });
            }
          }}>
          {t('ORGANISATION.TEAMS.CREATE_BUTTON_LABEL')}
        </Button>
      </Modal>
    </>
  );
}
